import {
  ApiError,
  queryKeys,
  type StaffNotificationPrefs,
  staffNotificationPrefsSchema,
  type UpdateStaffNotificationPrefsInput,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Switch,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { isAlertSoundEnabled, playAlertChime, setAlertSoundEnabled } from '../lib/alert-sound.js'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'
import {
  getCurrentPushSubscription,
  getNotificationPermission,
  getPushSupport,
  PushPermissionError,
  subscribeToPush,
  unsubscribeFromPush,
} from '../lib/push.js'

const PUSH_DEVICE_KEY = ['push-subscription', 'this-device'] as const

function errorMessage(error: unknown): string | undefined {
  return error instanceof ApiError || error instanceof PushPermissionError
    ? error.message
    : undefined
}

function SettingRow({
  title,
  description,
  children,
  footer,
}: {
  title: string
  description: React.ReactNode
  children: React.ReactNode
  footer?: React.ReactNode
}) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium">{title}</p>
          <p className="mt-0.5 text-xs text-foreground-muted">{description}</p>
        </div>
        <div className="shrink-0">{children}</div>
      </div>
      {footer ? <div className="mt-3">{footer}</div> : null}
    </div>
  )
}

/**
 * How this staff member hears about a new support ticket (t1078). Email, text
 * and push are saved to their account; the sound is a per-browser choice and
 * push is per device, since each phone, tablet and computer subscribes for
 * itself. The in-portal pop-up is always on and has no switch.
 */
export function NotificationSettingsTab() {
  const { staff, can } = useAuth()
  const canTickets = can('support:read')
  const canWorkspace = can('workspace:read')
  const canTasks = can('tasks:read')
  const toast = useToast()
  const queryClient = useQueryClient()
  const [soundOn, setSoundOn] = useState(isAlertSoundEnabled)
  // null while untouched, so the field follows what is saved.
  const [numberDraft, setNumberDraft] = useState<string | null>(null)

  const prefs = useQuery({
    queryKey: queryKeys.notificationPrefs(),
    queryFn: () =>
      api.get('/me/notification-preferences', { schema: staffNotificationPrefsSchema }),
  })

  const save = useMutation({
    mutationFn: (patch: UpdateStaffNotificationPrefsInput) =>
      api.patch<StaffNotificationPrefs>('/me/notification-preferences', patch, {
        schema: staffNotificationPrefsSchema,
      }),
    onSuccess: (next) => {
      queryClient.setQueryData(queryKeys.notificationPrefs(), next)
      toast.success('Saved')
    },
    onError: (error) => toast.error('Could not save', errorMessage(error)),
  })

  const support = getPushSupport()
  const permission = getNotificationPermission()

  const device = useQuery({
    queryKey: PUSH_DEVICE_KEY,
    queryFn: async () => (await getCurrentPushSubscription()) !== null,
    enabled: support === 'supported',
  })

  const enableDevice = useMutation({
    mutationFn: subscribeToPush,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PUSH_DEVICE_KEY })
      // Turning a device on is pointless while push is paused account-wide.
      if (prefs.data && !prefs.data.newTicketPush) save.mutate({ newTicketPush: true })
      else toast.success('Notifications are on for this device')
    },
    onError: (error) => toast.error('Could not turn on notifications', errorMessage(error)),
  })

  const disableDevice = useMutation({
    mutationFn: unsubscribeFromPush,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: PUSH_DEVICE_KEY })
      toast.success('Notifications are off for this device')
    },
    onError: (error) => toast.error('Could not turn off notifications', errorMessage(error)),
  })

  const loaded = prefs.data
  const busy = save.isPending
  const savedNumber = loaded?.newTicketSmsNumber ?? ''
  const shownNumber = numberDraft ?? savedNumber
  const numberChanged = numberDraft !== null && numberDraft.trim() !== savedNumber

  return (
    <div className="space-y-5">
      <Card className="max-w-xl">
        <CardHeader>
          <CardTitle>This device</CardTitle>
          <CardDescription>
            Each phone, tablet and computer turns on notifications for itself. Once on, you get a
            pop-up for new tickets, missed calls, notes, tasks and messages, and for an incoming
            call whenever the portal is in the background.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-md border border-border p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium">This device</p>
                <p className="mt-0.5 text-xs text-foreground-muted">
                  {support === 'unsupported'
                    ? "This browser can't show notifications."
                    : support === 'needs-install'
                      ? 'Not available until the portal is installed (see below).'
                      : permission === 'denied'
                        ? 'Blocked for this site in your browser settings.'
                        : device.data
                          ? 'Notifications are on for this device.'
                          : 'Notifications are off for this device.'}
                </p>
              </div>
              {support === 'supported' && permission !== 'denied' ? (
                device.data ? (
                  <div className="flex items-center gap-2">
                    <Badge tone="success">On</Badge>
                    <Button
                      size="sm"
                      loading={disableDevice.isPending}
                      onClick={() => disableDevice.mutate()}
                    >
                      Turn off
                    </Button>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="primary"
                    loading={enableDevice.isPending}
                    disabled={device.isLoading}
                    onClick={() => enableDevice.mutate()}
                  >
                    Turn on
                  </Button>
                )
              ) : null}
            </div>

            {support === 'needs-install' ? (
              <Alert tone="info" className="mt-3">
                On iPhone and iPad, notifications only work once this portal is added to your Home
                Screen. In Safari, tap Share, then Add to Home Screen. Open the portal from the new
                icon, sign in, and come back to this page to turn notifications on.
              </Alert>
            ) : null}
            {support === 'supported' && permission === 'denied' ? (
              <Alert tone="warning" className="mt-3">
                Notifications are blocked for this site. Allow them in your browser's site settings
                for this address, then reload this page.
              </Alert>
            ) : null}
          </div>
        </CardContent>
      </Card>

      {canTickets ? (
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>New ticket alerts</CardTitle>
            <CardDescription>
              A pop-up always appears here when a customer ticket arrives — by text, call or website
              chat — while the portal is open. Choose what else should reach you, and where.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <SettingRow
              title="Play a sound in this browser"
              description={
                <>
                  A short chime with each pop-up. Saved for this browser only.{' '}
                  <button
                    type="button"
                    className="text-primary underline-offset-4 hover:underline"
                    onClick={() => void playAlertChime({ force: true })}
                  >
                    Play a test sound
                  </button>
                </>
              }
            >
              <Switch
                checked={soundOn}
                aria-label="Play a sound in this browser"
                onCheckedChange={(checked) => {
                  setAlertSoundEnabled(checked)
                  setSoundOn(checked)
                }}
              />
            </SettingRow>

            <SettingRow
              title="Notifications on your devices"
              description="Banners in the notification area of your phone, tablet or computer, even when this portal is closed. Turn each device on below."
            >
              <Switch
                checked={loaded?.newTicketPush ?? true}
                disabled={!loaded || busy}
                aria-label="Notifications on your devices"
                onCheckedChange={(checked) => save.mutate({ newTicketPush: checked })}
              />
            </SettingRow>

            <SettingRow
              title="Email"
              description={
                <>Sent to {staff?.email ?? 'your account email'} for every new ticket.</>
              }
            >
              <Switch
                checked={loaded?.newTicketEmail ?? false}
                disabled={!loaded || busy}
                aria-label="Email alerts"
                onCheckedChange={(checked) => save.mutate({ newTicketEmail: checked })}
              />
            </SettingRow>

            <SettingRow
              title="Text message"
              description="A short text with the ticket number and a link, sent to the mobile number below."
              footer={
                <div className="flex items-center gap-2">
                  <Input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="(555) 555-0123"
                    aria-label="Mobile number for text alerts"
                    value={shownNumber}
                    disabled={!loaded}
                    onChange={(event) => setNumberDraft(event.target.value)}
                  />
                  <Button
                    size="sm"
                    disabled={!numberChanged || busy}
                    onClick={() =>
                      save.mutate(
                        {
                          newTicketSmsNumber:
                            (numberDraft ?? '').trim() === '' ? null : numberDraft,
                        },
                        { onSuccess: () => setNumberDraft(null) },
                      )
                    }
                  >
                    Save number
                  </Button>
                </div>
              }
            >
              <Switch
                checked={loaded?.newTicketSms ?? false}
                disabled={!loaded || busy || (!loaded.newTicketSms && !loaded.newTicketSmsNumber)}
                aria-label="Text message alerts"
                onCheckedChange={(checked) => save.mutate({ newTicketSms: checked })}
              />
            </SettingRow>
            <SettingRow
              title="Missed calls: notifications on your devices"
              description="A banner when a call comes in and nobody picks up. Needs this device turned on above."
            >
              <Switch
                checked={loaded?.missedCallPush ?? true}
                disabled={!loaded || busy}
                aria-label="Missed call notifications on your devices"
                onCheckedChange={(checked) => save.mutate({ missedCallPush: checked })}
              />
            </SettingRow>

            <SettingRow
              title="Notes on your tickets: notifications on your devices"
              description="A banner when a colleague leaves an internal note on a ticket assigned to you."
            >
              <Switch
                checked={loaded?.ticketNotePush ?? true}
                disabled={!loaded || busy}
                aria-label="Ticket note notifications on your devices"
                onCheckedChange={(checked) => save.mutate({ ticketNotePush: checked })}
              />
            </SettingRow>
          </CardContent>
        </Card>
      ) : null}

      {canWorkspace ? (
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>Workspace messages</CardTitle>
            <CardDescription>
              Hear about new Workspace messages without keeping the page open. You are never alerted
              about your own messages.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <SettingRow
              title="Direct messages: notifications on your devices"
              description="A banner when a colleague sends you a direct message. Needs this device turned on above."
            >
              <Switch
                checked={loaded?.workspaceDmPush ?? true}
                disabled={!loaded || busy}
                aria-label="Direct message notifications on your devices"
                onCheckedChange={(checked) => save.mutate({ workspaceDmPush: checked })}
              />
            </SettingRow>
            <SettingRow
              title="Direct messages: email"
              description={
                <>
                  Sent to {staff?.email ?? 'your account email'}, at most once per conversation
                  every 15 minutes.
                </>
              }
            >
              <Switch
                checked={loaded?.workspaceDmEmail ?? false}
                disabled={!loaded || busy}
                aria-label="Direct message email alerts"
                onCheckedChange={(checked) => save.mutate({ workspaceDmEmail: checked })}
              />
            </SettingRow>
            <SettingRow
              title="Channels and boards: notifications on your devices"
              description="A banner for every new channel message and board post. Off by default because it reaches everyone."
            >
              <Switch
                checked={loaded?.workspaceChannelPush ?? false}
                disabled={!loaded || busy}
                aria-label="Channel and board notifications on your devices"
                onCheckedChange={(checked) => save.mutate({ workspaceChannelPush: checked })}
              />
            </SettingRow>
          </CardContent>
        </Card>
      ) : null}

      {canTasks ? (
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>Tasks</CardTitle>
            <CardDescription>
              Choose how task updates reach you: a banner on your devices, and an email when a task
              you created is finished.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <SettingRow
              title="Task updates: notifications on your devices"
              description="A banner when a task you created is completed, a task needs your review, someone replies on your task, or a task is sent back to you."
            >
              <Switch
                checked={loaded?.taskPush ?? true}
                disabled={!loaded || busy}
                aria-label="Task update notifications on your devices"
                onCheckedChange={(checked) => save.mutate({ taskPush: checked })}
              />
            </SettingRow>
            <SettingRow
              title="Task completed: email"
              description={
                <>
                  Sent to {staff?.email ?? 'your account email'} when someone else completes a task
                  you created. The note in Recent notifications on your dashboard always appears.
                </>
              }
            >
              <Switch
                checked={loaded?.taskCompletedEmail ?? true}
                disabled={!loaded || busy}
                aria-label="Task completed email"
                onCheckedChange={(checked) => save.mutate({ taskCompletedEmail: checked })}
              />
            </SettingRow>
          </CardContent>
        </Card>
      ) : null}
    </div>
  )
}
